import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, Navigate } from 'react-router-dom';
import { BrandLogo } from '../components/BrandLogo';
import { ArrowRight, ArrowLeft, Wifi, Radio, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, ChevronRight, Building2, Sparkles, Download, Linkedin } from 'lucide-react';
import useCardShare from '../hooks/useCardShare';

const API = process.env.REACT_APP_BACKEND_URL;

const COUNCIL_META = {
  stockton:      { name: 'Stockton-on-Tees',      logo: '/councils/stockton.png',           invert: false, tagline: 'Stockton-on-Tees Borough Council' },
  middlesbrough: { name: 'Middlesbrough',          logo: '/councils/middlesbrough.png',      invert: true,  tagline: 'Middlesbrough Council' },
  redcar:        { name: 'Redcar and Cleveland',   logo: '/councils/redcar-cleveland.jpg',   invert: false, tagline: 'Redcar & Cleveland Borough Council' },
  hartlepool:    { name: 'Hartlepool',             logo: '/councils/hartlepool.jpg',         invert: false, tagline: 'Hartlepool Borough Council' },
  darlington:    { name: 'Darlington',             logo: '/councils/darlington.png',         invert: true,  tagline: 'Darlington Borough Council' },
  tvca:          { name: 'Tees Valley',            logo: null,                                invert: false, tagline: 'Tees Valley Combined Authority' },
};

function Stat({ label, value, sub, accent = false, testid }) {
  return (
    <div className="backdrop-blur-2xl bg-white/60 border border-white/50 shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-5 rounded-[4px]" data-testid={testid}>
      <div className={`font-display text-3xl sm:text-4xl font-extrabold tracking-tight ${accent ? 'text-[#FE4A2A]' : 'text-[#090909]'}`}>{value}</div>
      <div className="font-body text-xs text-[#5A5751]/80 mt-2 leading-snug">{label}</div>
      {sub && <div className="font-body text-[10px] text-[#5A5751]/50 mt-1 uppercase tracking-wider">{sub}</div>}
    </div>
  );
}

function Ring({ percent, label, sub, accent = '#FE4A2A' }) {
  const radius = 40, circ = 2 * Math.PI * radius;
  const dash = (percent / 100) * circ;
  return (
    <div className="flex flex-col items-center">
      <svg width="100" height="100" viewBox="0 0 100 100" className="-rotate-90">
        <circle cx="50" cy="50" r={radius} fill="none" stroke="#F5F4F0" strokeWidth="8" />
        <circle cx="50" cy="50" r={radius} fill="none" stroke={accent} strokeWidth="8" strokeLinecap="round" strokeDasharray={`${dash} ${circ}`} />
      </svg>
      <div className="font-display text-lg font-bold text-[#090909] -mt-14">{percent.toFixed(1)}%</div>
      <div className="mt-10 text-center">
        <div className="font-body text-xs text-[#090909] font-semibold">{label}</div>
        <div className="font-body text-[10px] text-[#5A5751]/60 uppercase tracking-wider mt-0.5">{sub}</div>
      </div>
    </div>
  );
}

export default function AuthorityPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [authority, setAuthority] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/authority/${slug}` : '';
  const shareCaption = authority
    ? `${authority.la_name || slug}: ${(stats?.total_businesses || 0).toLocaleString()} registered businesses. ${(stats?.digitally_invisible || 0).toLocaleString()} digitally invisible. Coverage is not connectivity. Made visible by Adoptic.`
    : '';
  const share = useCardShare({
    target: slug,
    cardPath: 'borough-reality.png',
    shareUrl,
    caption: shareCaption,
    filename: `adoptic-${slug}-reality.png`,
  });

  useEffect(() => {
    if (!COUNCIL_META[slug]) { setError('unknown'); return; }
    Promise.all([
      fetch(`${API}/api/authorities/${slug}`).then(r => r.json()),
      fetch(`${API}/api/public/stats?la=${slug}`).then(r => r.json()),
    ]).then(([a, s]) => { setAuthority(a); setStats(s); }).catch(e => setError(e.message));
  }, [slug]);

  if (!COUNCIL_META[slug]) return <Navigate to="/" replace />;
  if (error === 'unknown') return <Navigate to="/" replace />;

  if (!authority || !stats) return (
    <div className="min-h-screen bg-[#F5F4F0] flex items-center justify-center">
      <div className="w-8 h-8 rounded-[4px] bg-brand-orange animate-pulse" />
    </div>
  );

  const meta = COUNCIL_META[slug];
  const ofcomFixed = authority.ofcom_fixed || {};
  const ofcomMobile = authority.ofcom_mobile || {};
  const imd = authority.imd_2025_summary || {};
  const bdafBreakdown = stats.bdaf_status_breakdown || {};
  const invisiblePct = Math.round(stats.digitally_invisible / Math.max(stats.total_businesses, 1) * 100);
  const hi = authority.headline_insights || null;
  const hiReg = hi?.register;
  const hiTrad = hi?.trading;
  const hiSignals = hi?.signals;
  const hiSegments = hi?.segments;
  const corrImd = hi?.correlations?.imd_vs_silence;
  const corrSpeed = hi?.correlations?.speed_vs_silence;

  return (
    <div className="min-h-screen bg-[#FEFEFE] text-[#090909]" data-testid={`authority-page-${slug}`}>
      {/* Grain overlay */}
      <div className="fixed inset-0 pointer-events-none z-50 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }} />

      {/* TOP NAV — co-brand */}
      <nav className="sticky top-0 z-40 bg-[#FEFEFE]/90 backdrop-blur-md border-b border-[#090909]/8" data-testid="authority-nav">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center" data-testid="authority-back-link">
              <ArrowLeft className="w-3.5 h-3.5 text-[#5A5751]" />
            </Link>
            <BrandLogo className="h-5 w-auto brightness-0" testId="authority-brand" />
            <span className="hidden sm:inline-block w-px h-4 bg-[#090909]/20" />
            <div className="flex items-center gap-2">
              <div className={`h-6 flex items-center ${meta.invert ? 'bg-[#090909] px-2 rounded-[3px]' : ''}`}>
                <img src={meta.logo} alt={meta.tagline} className={`h-4 w-auto ${meta.invert ? '' : ''}`} data-testid={`council-logo-${slug}`} />
              </div>
              <span className="font-body text-[11px] uppercase tracking-[0.14em] text-[#5A5751] hidden md:inline">{meta.tagline}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/login')} className="font-body text-xs text-[#090909]/60 hover:text-[#090909] px-3 py-1.5" data-testid="authority-signin">Sign In</button>
            <button onClick={() => navigate('/login')} className="inline-flex items-center gap-1 bg-[#090909] text-white px-4 py-1.5 rounded-[3px] font-body text-xs font-medium hover:bg-[#FE4A2A]" data-testid="authority-cta">
              Request Access <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative py-20" data-testid="authority-hero">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-20 right-20 w-[500px] h-[500px] bg-[#FE4A2A] rounded-full opacity-[0.04] blur-[120px]" />
        </div>
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-6">
            <Link to="/" className="font-body text-xs text-[#5A5751]/60 hover:text-[#FE4A2A]" data-testid="authority-crumb-home">Tees Valley Digital Register</Link>
            <ChevronRight className="w-3 h-3 text-[#5A5751]/40" />
            <span className="font-body text-xs text-[#5A5751] font-semibold" data-testid="authority-crumb-current">{meta.name}</span>
          </div>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-[3.75rem] font-bold tracking-tight leading-[1.02] mb-6" data-testid="authority-title">
            {meta.name}.<br /><span className="text-[#FE4A2A]">Made visible.</span>
          </h1>

          <p className="font-body text-base sm:text-lg text-[#090909]/80 max-w-3xl mb-8 leading-relaxed" data-testid="authority-subtitle">
            Every registered business in {meta.name} — scored for digital presence, cross-referenced against Ofcom connectivity infrastructure, and mapped against MHCLG deprivation indices. Updated {new Date(authority.updated_at || Date.now()).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}.
          </p>

          {/* Hero counter pill */}
          <div className="inline-flex items-center gap-2 mb-10 px-3 py-1.5 rounded-full bg-[#FECFA5]/40 border border-[#FECFA5]" data-testid="authority-live-badge">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FE4A2A] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FE4A2A]" />
            </span>
            <span className="font-body text-[11px] text-[#742618] font-semibold">
              {stats.digitally_invisible.toLocaleString()} of {stats.total_businesses.toLocaleString()} scoring 0–1 · {invisiblePct}% digitally invisible
            </span>
          </div>

          {/* 4-up hero stat grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Stat testid="stat-total" value={stats.total_businesses.toLocaleString()} label={`Registered businesses in ${meta.name}`} sub="Companies House" accent />
            <Stat testid="stat-invisible" value={`${invisiblePct}%`} label="Trading with zero digital presence" sub="BDAF score 0–1" accent />
            <Stat testid="stat-idbr" value={(stats.idbr_estimated || 0).toLocaleString()} label="ONS estimated total (IDBR baseline)" sub={`+${(stats.unregistered_estimated || 0).toLocaleString()} untracked`} />
            <Stat testid="stat-bdaf" value={(stats.avg_bdaf_score || 0).toFixed(2)} label="Average BDAF score" sub="Reconciled 0–5 scale" />
          </div>

          <div className="flex flex-wrap gap-3">
            <button data-testid="authority-cta-primary" onClick={() => navigate('/login')} className="flex items-center gap-2 bg-[#FE4A2A] text-white px-6 py-3 rounded-[2px] font-body text-sm font-medium hover:bg-[#F14728]">
              Access the {meta.name} Register <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={share.downloadPng} className="flex items-center gap-2 border border-[#090909]/15 px-6 py-3 rounded-[2px] font-body text-sm text-[#5A5751] hover:border-[#090909]/30" data-testid="authority-share-download">
              <Download className="w-4 h-4" /> Download share card
            </button>
            <button onClick={share.openLinkedIn} className="flex items-center gap-2 border border-[#090909]/15 px-6 py-3 rounded-[2px] font-body text-sm text-[#5A5751] hover:border-[#090909]/30" data-testid="authority-share-linkedin">
              <Linkedin className="w-4 h-4" /> Share on LinkedIn
            </button>
          </div>
        </div>
      </section>

      {/* CONNECTIVITY & DEPRIVATION */}
      <section className="py-16 bg-[#F5F4F0] border-y border-[#090909]/8" data-testid="authority-connectivity">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="mb-8">
            <span className="font-body text-[11px] uppercase tracking-[0.18em] text-[#FE4A2A] font-bold">Infrastructure Reality</span>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold mt-2">{meta.name} has world-class connectivity. But adoption tells a different story.</h2>
            <p className="font-body text-sm text-[#5A5751] mt-3 max-w-2xl">Ofcom Connected Nations, January 2026. MHCLG Index of Multiple Deprivation, 2025.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-10">
            <Ring percent={ofcomFixed.gigabit_pct || 0} label="Gigabit-capable" sub="of premises" />
            <Ring percent={ofcomFixed.full_fibre_pct || 0} label="Full Fibre" sub="of premises" />
            <Ring percent={ofcomFixed.ufbb_pct || 0} label="Ultrafast" sub="of premises" />
            <Ring percent={ofcomMobile['4g_outdoor_pct'] || 99} label="4G Outdoor" sub="of premises" accent="#2D6E4E" />
            <Ring percent={100 - (ofcomFixed.below_uso_pct || 0)} label="Meets USO" sub="10 Mbit/s+" accent="#2D6E4E" />
          </div>

          {imd.imd_rank_of_average_rank_2025 && (
            <div className="bg-white p-6 rounded-[4px] border border-[#090909]/8 max-w-3xl" data-testid="authority-imd-card">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-8 h-8 text-[#FE4A2A] shrink-0" strokeWidth={1.5} />
                <div>
                  <h3 className="font-display text-lg font-bold mb-1">Deprivation ranks {imd.imd_rank_of_average_rank_2025}<sup>st</sup> of 296 LAs nationally</h3>
                  <p className="font-body text-sm text-[#5A5751] leading-relaxed">
                    MHCLG IoD 2025 places {meta.name} in the most deprived {Math.round(imd.imd_rank_of_average_rank_2025 / 296 * 100)}% of English local authorities. Average IMD score: {imd.imd_average_score_2025?.toFixed(2)}. Yet our internal correlation shows deprivation explains almost none of the digital silence here (r&nbsp;=&nbsp;{corrImd?.r_business ?? 0.04} at business level) — the exclusion is structural, not socioeconomic, and present in every neighbourhood.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* BDAF DISTRIBUTION */}
      <section className="py-16" data-testid="authority-bdaf">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <span className="font-body text-[11px] uppercase tracking-[0.18em] text-[#FE4A2A] font-bold">Business Digital Adoption Framework</span>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold mt-2 mb-6">6 signals. 6 tiers. {stats.total_businesses.toLocaleString()} businesses.</h2>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {['Critical', 'At Risk', 'Invisible', 'Emerging', 'Active', 'AI-Ready'].map((status, i) => {
              const n = bdafBreakdown[status] || 0;
              const pct = Math.round(n / Math.max(stats.total_businesses, 1) * 100);
              const colors = ['#8B2416', '#C94F1A', '#E88034', '#F4B860', '#7BA85C', '#2D6E4E'];
              return (
                <div key={status} className="bg-white border border-[#090909]/8 p-4 rounded-[4px]" data-testid={`bdaf-tier-${status.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                  <div className="w-3 h-3 rounded-full mb-2" style={{ background: colors[i] }} />
                  <div className="font-display text-2xl font-extrabold">{n.toLocaleString()}</div>
                  <div className="font-body text-[10px] uppercase tracking-wider text-[#5A5751]/60 mt-1">{status}</div>
                  <div className="font-body text-[11px] text-[#5A5751] mt-1">{pct}% of register</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* REGISTER HEADLINE — BDAF FINAL ANALYSIS */}
      {hi && (
        <section className="py-16 bg-white border-t border-[#090909]/8" data-testid="authority-headline">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
            <span className="font-body text-[11px] uppercase tracking-[0.18em] text-[#FE4A2A] font-bold">Register Headline — BDAF Final Analysis</span>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold mt-2 mb-2">The real numbers for {meta.name}.</h2>
            <p className="font-body text-sm text-[#5A5751] mb-8 max-w-2xl">{hi.basis}.</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-[#FAFAF8] border border-[#090909]/8 p-6 rounded-[4px]" data-testid="authority-headline-register">
                <div className="font-body text-[9px] uppercase tracking-[0.15em] text-[#FE4A2A] mb-3">All register</div>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="font-display text-4xl font-extrabold">{hiReg.mean_bdaf}</span>
                  <span className="font-body text-xs text-[#5A5751]">mean BDAF / 5</span>
                </div>
                <div className="space-y-1.5 font-body text-[11px] text-[#5A5751]">
                  <div className="flex justify-between"><span>Businesses</span><span className="font-medium text-[#090909]">{hiReg.total.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span>Scoring 0–1</span><span className="font-medium text-[#8A1A1A]">{hiReg.count_0_1.toLocaleString()} ({hiReg.pct_0_1}%)</span></div>
                  <div className="flex justify-between"><span>Google-verified</span><span className="font-medium text-[#090909]">{hiReg.gmb_verified_pct}%</span></div>
                  <div className="flex justify-between"><span>Customer reviews</span><span className="font-medium text-[#090909]">{hiReg.total_reviews.toLocaleString()}</span></div>
                </div>
              </div>
              <div className="bg-[#FAFAF8] border border-[#090909]/8 p-6 rounded-[4px]" data-testid="authority-headline-trading">
                <div className="font-body text-[9px] uppercase tracking-[0.15em] text-[#2D6E4E] mb-3">Trading only ({hiTrad.pct_of_register}% of register)</div>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="font-display text-4xl font-extrabold">{hiTrad.mean_bdaf}</span>
                  <span className="font-body text-xs text-[#5A5751]">mean BDAF / 5</span>
                </div>
                <div className="space-y-1.5 font-body text-[11px] text-[#5A5751]">
                  <div className="flex justify-between"><span>Trading businesses</span><span className="font-medium text-[#090909]">{hiTrad.count.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span>Wholly invisible (score 0)</span><span className="font-medium text-[#8A1A1A]">{hiTrad.invisible_0_count.toLocaleString()} ({hiTrad.invisible_0_pct}%)</span></div>
                  <div className="flex justify-between"><span>Scoring 0–1</span><span className="font-medium text-[#8A1A1A]">{hiTrad.pct_0_1}%</span></div>
                  <div className="flex justify-between"><span>AI-Ready (all 5 signals)</span><span className="font-medium text-[#2D6E4E]">{hiTrad.ai_ready_count.toLocaleString()} ({hiTrad.ai_ready_pct}%)</span></div>
                </div>
              </div>
              <div className="bg-[#FAFAF8] border border-[#090909]/8 p-6 rounded-[4px]" data-testid="authority-headline-signals">
                <div className="font-body text-[9px] uppercase tracking-[0.15em] text-[#5A5751] mb-3">Signal penetration — trading</div>
                <div className="space-y-2">
                  {[
                    ['S1 Website', hiSignals.website_pct],
                    ['S2 Google profile', hiSignals.google_business_pct],
                    ['S5 Reviews', hiSignals.reviews_pct],
                    ['S4 Social media', hiSignals.social_pct],
                    ['S3 Directories', hiSignals.directories_pct],
                  ].map(([label, v], i) => (
                    <div key={i}>
                      <div className="flex justify-between font-body text-[10px] text-[#5A5751] mb-0.5">
                        <span>{label}</span><span className="font-medium text-[#090909]">{v}%</span>
                      </div>
                      <div className="h-1.5 bg-[#090909]/5 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${v}%`, background: v >= 65 ? '#2D6E4E' : v >= 45 ? '#8A5A14' : '#8A1A1A' }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Internal correlations + opportunity segments */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="authority-correlations">
              <div className="border border-[#090909]/8 p-6 rounded-[4px]">
                <div className="font-body text-[9px] uppercase tracking-[0.15em] text-[#5A5751] mb-4">Internal correlations — what drives silence here</div>
                <div className="flex gap-8">
                  <div>
                    <div className="font-display text-3xl font-extrabold text-[#FE4A2A]">r = {corrImd?.r_business ?? '—'}</div>
                    <div className="font-body text-[10px] text-[#5A5751] mt-1 max-w-[180px]">IMD 2025 deprivation × scoring 0–1{corrImd?.r_district != null ? ` (district r = ${corrImd.r_district})` : ''}</div>
                  </div>
                  <div>
                    <div className="font-display text-3xl font-extrabold text-[#090909]">r = {corrSpeed?.r_business ?? '—'}</div>
                    <div className="font-body text-[10px] text-[#5A5751] mt-1 max-w-[180px]">Broadband speed × scoring 0–1{corrSpeed?.r_district != null ? ` (district r = ${corrSpeed.r_district})` : ''}</div>
                  </div>
                </div>
                <p className="font-body text-[11px] text-[#5A5751] mt-4 leading-relaxed">
                  Neither deprivation nor broadband speed explains digital silence in {meta.name}. It is structural — driven by sector and company age — which is precisely why targeted, evidence-led intervention works.
                </p>
              </div>
              <div className="border border-[#090909]/8 p-6 rounded-[4px]" data-testid="authority-segments">
                <div className="font-body text-[9px] uppercase tracking-[0.15em] text-[#5A5751] mb-4">Opportunity segments</div>
                <div className="space-y-3">
                  {[
                    [hiSegments?.website_no_gbp, 'have a website but no Google Business Profile — the highest-leverage free fix'],
                    [hiSegments?.gbp_no_website, 'have a Google profile but no owned website — platform-dependent'],
                    [hiSegments?.wholly_invisible, 'trading businesses with no digital presence at all — first-presence targets'],
                  ].map(([n, label], i) => (
                    <div key={i} className="flex items-baseline gap-3">
                      <span className="font-display text-2xl font-extrabold text-[#FE4A2A] min-w-[80px] shrink-0">{(n || 0).toLocaleString()}</span>
                      <span className="font-body text-[11px] text-[#5A5751] leading-snug">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* POSTCODE DISTRICT BREAKDOWN */}
      <section className="py-16 bg-[#F5F4F0] border-t border-[#090909]/8" data-testid="authority-districts">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <span className="font-body text-[11px] uppercase tracking-[0.18em] text-[#FE4A2A] font-bold">Postcode-level Reality</span>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold mt-2 mb-6">{(stats.districts || []).length} postcode districts across {meta.name}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {(stats.districts || []).slice(0, 24).map(d => {
              const adoption = d.adoption_rate || 0;
              return (
                <div key={d.district} className="bg-white p-4 rounded-[4px] border border-[#090909]/8 hover:border-[#FE4A2A]/40 transition-colors" data-testid={`district-${d.district}`}>
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="font-display text-lg font-bold">{d.district}</span>
                    <span className="font-body text-xs text-[#5A5751]/60">{d.total.toLocaleString()} biz</span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex-1 h-1.5 bg-[#F5F4F0] rounded-full overflow-hidden">
                      <div className="h-full" style={{ width: `${adoption}%`, background: adoption > 30 ? '#2D6E4E' : adoption > 15 ? '#F4B860' : '#FE4A2A' }} />
                    </div>
                    <span className="font-body text-xs font-semibold text-[#090909]">{adoption.toFixed(0)}%</span>
                  </div>
                  <div className="flex gap-3 mt-2 font-body text-[10px] text-[#5A5751]/70">
                    <span><CheckCircle2 className="w-3 h-3 inline mr-0.5 text-[#2D6E4E]" strokeWidth={2} />{d.active}</span>
                    <span>~ {d.partial}</span>
                    <span><AlertTriangle className="w-3 h-3 inline mr-0.5 text-[#FE4A2A]" strokeWidth={2} />{d.invisible}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" data-testid="authority-cta-section">
        <div className="max-w-[900px] mx-auto px-6 lg:px-8 text-center">
          <Sparkles className="w-10 h-10 text-[#FE4A2A] mx-auto mb-4" strokeWidth={1.5} />
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter leading-tight mb-4">
            Every business in {meta.name}.<br /><span className="text-[#FE4A2A]">Every intervention. Evidenced.</span>
          </h2>
          <p className="font-body text-base text-[#5A5751] max-w-2xl mx-auto mb-8 leading-relaxed">
            The full {meta.name} Digital Register — Intervention Planner, Priority Matrix, ROI Estimator, and HM Green Book auto-reports — is available to {meta.tagline} teams and delivery partners on subscription.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button onClick={() => navigate('/pricing')} className="flex items-center gap-2 bg-[#FE4A2A] text-white px-6 py-3 rounded-[2px] font-body text-sm font-medium hover:bg-[#F14728]" data-testid="authority-view-pricing">
              View pricing <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => navigate('/login')} className="flex items-center gap-2 border border-[#090909]/15 px-6 py-3 rounded-[2px] font-body text-sm hover:border-[#090909]/30" data-testid="authority-request-demo">
              Request council demo
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#090909]/8 py-8" data-testid="authority-footer">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <BrandLogo className="h-4 w-auto brightness-0" testId="authority-footer-brand" />
            <span className="w-px h-4 bg-[#090909]/15" />
            <span className="font-body text-[11px] text-[#5A5751]/70">Deployed for {meta.tagline}</span>
          </div>
          <div className="font-body text-[10px] text-[#5A5751]/60">
            Data: Companies House · Ofcom · MHCLG · Google Places · OGL v3.0 &nbsp;·&nbsp; © Nexintell Limited
          </div>
        </div>
      </footer>
    </div>
  );
}
